import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectTemplateDetailsResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import * as React from "react";
import { getUpdatedVersionMask, getVersionMask, mapMaskToUpdateBehaviour, mapMaskToUsageConstraint, TemplateVersioningSection, UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import { useProjectContext } from "~/areas/projects/context/index";
import Markdown from "~/components/Markdown";
import { FormSection, FormSectionHeading } from "~/components/form/index";
interface ProjectTemplateVersioningSectionProps {
    onProjectTemplateDetailsChange?: (updates: Partial<ProjectTemplateDetailsResource>) => void;
    projectTemplateDetails: ProjectTemplateDetailsResource;
}
export function ProjectTemplateTitleSection({ onProjectTemplateDetailsChange, projectTemplateDetails }: ProjectTemplateVersioningSectionProps) {
    const { state } = useProjectContext();
    const projectTemplateSlug = projectTemplateDetails?.Slug;
    const [selectedUsageConstraint, setSelectedUsageConstraint] = useState<UsageConstraint>(projectTemplateDetails?.VersionMask ? mapMaskToUsageConstraint(projectTemplateDetails.VersionMask) : UsageConstraint.Stable);
    const [selectedUpdateBehaviour, setSelectedUpdateBehaviour] = useState<UpdateBehaviour>(projectTemplateDetails?.VersionMask ? mapMaskToUpdateBehaviour(projectTemplateDetails.VersionMask) : UpdateBehaviour.Minor);
    const { result: projectTemplateSummary } = useQuery(async (repository) => {
        if (!state.model?.Id) {
            return null;
        }
        return await repository.TemplatedProjects.getProjectTemplateSummary(state.model.Id);
    }, [state.model?.Id], "Get project template summary");
    const { result: latestVersionSummary } = useQuery(async (repository) => {
        if (!projectTemplateSlug) {
            return null;
        }
        const processVersionSummary = await repository.TemplatedProjects.getLatestVersionSummary(projectTemplateSlug);
        return {
            ProjectTemplateSlug: projectTemplateSlug,
            LatestStableVersion: processVersionSummary.LatestStableVersion,
            LatestPreReleaseVersion: processVersionSummary.LatestPreReleaseVersion,
            IsOnlyPreRelease: processVersionSummary.IsOnlyPreRelease,
        };
    }, [projectTemplateSlug], "Get latest project template version summary");
    const updateProjectTemplateDetails = React.useCallback((updates: Partial<ProjectTemplateDetailsResource>) => {
        if (onProjectTemplateDetailsChange) {
            onProjectTemplateDetailsChange(updates);
        }
    }, [onProjectTemplateDetailsChange]);
    const handleUpdateToLatestVersion = () => {
        const latestStableVersion = latestVersionSummary?.LatestStableVersion;
        if (!latestStableVersion) {
            return;
        }
        if (selectedUsageConstraint === UsageConstraint.PreRelease) {
            setSelectedUsageConstraint(UsageConstraint.Stable);
            setSelectedUpdateBehaviour(UpdateBehaviour.Minor);
            const versionMask = getVersionMask(latestStableVersion, UpdateBehaviour.Minor);
            updateProjectTemplateDetails({
                Version: latestStableVersion,
                VersionMask: versionMask,
            });
        }
        else {
            const versionMask = getVersionMask(latestStableVersion, selectedUpdateBehaviour);
            updateProjectTemplateDetails({
                Version: latestStableVersion,
                VersionMask: versionMask,
            });
        }
    };
    const onUpdateBehaviourChanged = (value: UpdateBehaviour) => {
        const latestStableVersion = latestVersionSummary?.LatestStableVersion;
        if (!latestStableVersion) {
            throw Error("Cannot change update behaviour when no stable version is available.");
        }
        setSelectedUpdateBehaviour(value);
        const initialUpdateBehavior = projectTemplateDetails?.VersionMask ? mapMaskToUpdateBehaviour(projectTemplateDetails.VersionMask) : UpdateBehaviour.Minor;
        const newVersionMask = getUpdatedVersionMask(latestStableVersion, projectTemplateDetails?.Version, value, initialUpdateBehavior);
        updateProjectTemplateDetails({ VersionMask: newVersionMask });
    };
    const onUsageConstraintChanged = (value: UsageConstraint) => {
        if (value === UsageConstraint.PreRelease) {
            updateProjectTemplateDetails({ VersionMask: UpdateBehaviour.PreRelease });
            setSelectedUsageConstraint(UsageConstraint.PreRelease);
        }
        else {
            setSelectedUsageConstraint(value);
            onUpdateBehaviourChanged(UpdateBehaviour.Minor);
        }
    };
    if (!projectTemplateSlug || !latestVersionSummary || !projectTemplateSummary) {
        return null;
    }
    return (<>
            <FormSectionHeading title="Project template details"/>
            <FormSection includeBorder={true} title="Name" help={projectTemplateSummary.Name} key="Name"/>
            {projectTemplateSummary.Description ? <FormSection title="Description" includeBorder={true} help={<Markdown markup={projectTemplateSummary.Description}/>}></FormSection> : null}

            <TemplateVersioningSection templateName={projectTemplateSummary.Name} currentVersion={projectTemplateDetails.Version || ""} latestStableVersion={latestVersionSummary.LatestStableVersion} latestPreReleaseVersion={latestVersionSummary.LatestPreReleaseVersion} isOnlyPreRelease={latestVersionSummary.IsOnlyPreRelease} selectedUsageConstraint={selectedUsageConstraint} selectedUpdateBehaviour={selectedUpdateBehaviour} onUsageConstraintChanged={onUsageConstraintChanged} onUpdateBehaviourChanged={onUpdateBehaviourChanged} onUpdateToLatestVersion={handleUpdateToLatestVersion} isFeatureEnabled={true}/>
        </>);
}
